import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import { ChatbotService } from "../../services/chatbot-service";
import chatbotConfig from "../../components/ChatbotWidget/chatbot-config.json"

export enum Sender {
    USER = "user",
    BOT = "bot",
}

export interface Message {
    id: string;
    text: string;
    sender: Sender;
    loading?: boolean;
}

interface ChatbotContextValue {
    messages: Message[];
    showPrompts: boolean;
    isLoading: boolean;
    sendMessage: (text: string) => void;
    clearChat: () => void;
    setShowPrompts: (v: boolean) => void;
}

const ChatbotContext = createContext<ChatbotContextValue | null>(null);
const INITIAL_MESSAGE = chatbotConfig.initialMessage as Message;

export const useChatbot = () => {
    const context = useContext(ChatbotContext);
    if (!context) throw new Error("useChatbot must be used within ChatbotProvider");
    return context;
};

export const ChatbotProvider = ({ children }: { children: React.ReactNode }) => {
    const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
    const [showPrompts, setShowPrompts] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const requestIdRef = useRef(0);
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const sendMessage = useCallback((text: string) => {
        const userMsgId = crypto.randomUUID();
        const botMsgId = crypto.randomUUID();

        setMessages((prev) => [
            ...prev,
            { id: userMsgId, text, sender: Sender.USER },
            { id: botMsgId, text: "", sender: Sender.BOT, loading: true },
        ]);
        setIsLoading(true);
        setShowPrompts(false);
        const currentRequestId = ++requestIdRef.current;

        const conversationHistory = messagesRef.current
            .filter((msg) => msg.id !== "initial-message" && !msg.loading)
            .map((msg) => ({ text: msg.text, sender: msg.sender }));

        const updateBotMessage = (text: string) => {
            setMessages((prev) => {
                const messageIndex = prev.findIndex((msg) => msg.id === botMsgId);
                if (messageIndex === -1) return prev;
                const updated = [...prev];
                updated[messageIndex] = { ...prev[messageIndex], text, loading: false };
                return updated;
            });
            setIsLoading(false);
        };

        ChatbotService.instance.sendMessage(text, conversationHistory)
            .then((responseText) => {
                if (requestIdRef.current !== currentRequestId) return;
                updateBotMessage(responseText);
            })
            .catch(() => {
                if (requestIdRef.current !== currentRequestId) return;
                updateBotMessage(getString('chatbot.messages.error'));
            });
    }, []);

    const clearChat = useCallback(() => {
        requestIdRef.current++;
        setMessages([INITIAL_MESSAGE]);
        setShowPrompts(true);
        setIsLoading(false);
    }, []);

    const value = useMemo<ChatbotContextValue>(
        () => ({ messages, showPrompts, isLoading, sendMessage, clearChat, setShowPrompts }),
        [messages, showPrompts, isLoading, sendMessage, clearChat, setShowPrompts]
    );

    return (
        <ChatbotContext.Provider value={value}>
            {children}
        </ChatbotContext.Provider>
    );
};
